import { useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import AddProviderForm, { AddProviderFormValues } from "./AddProviderForm";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
import { mapProviderDetailToFormValues } from "../types/providerIntegrationtypes";
import { useAuditUserId } from "../../../hooks/useAuditUserId";

interface EditProviderModalProps {
  providerId: string | null;
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EditProviderModal = ({
  providerId,
  show,
  onClose,
  onSaved,
}: EditProviderModalProps) => {
  const auditUserId = useAuditUserId();
  const [initialValues, setInitialValues] = useState<AddProviderFormValues | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !providerId) {
      setInitialValues(null);
      setLoadError("");
      setSaveError("");
      return;
    }

    const id = Number(providerId);
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setSaveError("");

    ProviderIntegrationApi.getProvider(id)
      .then((detail) => {
        if (cancelled) return;
        setInitialValues(mapProviderDetailToFormValues(detail));
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Unable to load provider details.");
        setInitialValues(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, providerId]);

  const handleSubmit = async (values: AddProviderFormValues) => {
    const id = Number(providerId);
    if (!id || saving) return;

    setSaving(true);
    setSaveError("");
    try {
      await ProviderIntegrationApi.updateProvider(id, {
        providerName: values.name.trim(),
        description: values.description.trim() || undefined,
        status: values.status,
        subscriptionKey: values.subscriptionKey.trim(),
        updatedBy: auditUserId,
      });
      onSaved();
      onClose();
    } catch {
      setSaveError("Unable to save provider details. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      className="ic-provider-form-offcanvas"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Provider Details</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loading && (
          <div className="ic-modal-loading" role="status">
            <div className="spinner-border text-primary" aria-hidden="true" />
            <span>Loading provider details...</span>
          </div>
        )}
        {!loading && loadError && (
          <div className="ic-inline-message ic-inline-message-error" role="alert">
            {loadError}
          </div>
        )}
        {!loading && initialValues && (
          <>
            {saveError && (
              <div className="ic-inline-message ic-inline-message-error mb-3" role="alert">
                {saveError}
              </div>
            )}
            <AddProviderForm
              mode="edit"
              initialValues={initialValues}
              codeReadOnly
              isSubmitting={saving}
              onCancel={onClose}
              onSubmit={handleSubmit}
            />
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default EditProviderModal;
